/**
 * External links on the business page: the Zuora link (organizations.zuora_link) plus Agent Portal / Admin Portal /
 * other deep links stored in organization_external_links (staff-only RLS, migration 2026-10-05-303).
 * Every link opens in a new tab and is labeled.
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Card from "../ui/Card";
import Button from "../common/Button";
import Modal from "../ui/Modal";
import {
  EXTERNAL_LINK_PRESETS,
  createOrganizationExternalLink,
  deleteOrganizationExternalLink,
  getOrganizationExternalLinks,
  isValidExternalLinkUrl,
  updateOrganizationExternalLink,
} from "../../services/organizations.service";
import type { OrganizationExternalLink } from "../../services/organizations.service";

interface ExternalLinksCardProps {
  organizationId: string;
  zuoraLink?: string | null;
  canEdit: boolean;
}

const OTHER = "__other__";

const ExternalLink: React.FC<{ href: string; label: string }> = ({
  href,
  label,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
  >
    <span>{label}</span>
    <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
    <span className="sr-only">(opens in a new tab)</span>
  </a>
);

const ExternalLinksCard: React.FC<ExternalLinksCardProps> = ({
  organizationId,
  zuoraLink,
  canEdit,
}) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<OrganizationExternalLink | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [preset, setPreset] = useState<string>(EXTERNAL_LINK_PRESETS[0]);
  const [customLabel, setCustomLabel] = useState("");
  const [url, setUrl] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const queryKey = ["organization-external-links", organizationId];
  const { data: links = [], error: loadError } = useQuery({
    queryKey,
    queryFn: () => getOrganizationExternalLinks(organizationId),
    enabled: !!organizationId,
  });

  const close = () => {
    setIsOpen(false);
    setEditing(null);
    setFormError(null);
  };

  const openAdd = () => {
    setEditing(null);
    setPreset(EXTERNAL_LINK_PRESETS[0]);
    setCustomLabel("");
    setUrl("");
    setFormError(null);
    setIsOpen(true);
  };

  const openEdit = (link: OrganizationExternalLink) => {
    setEditing(link);
    const isPreset = (EXTERNAL_LINK_PRESETS as readonly string[]).includes(
      link.name,
    );
    setPreset(isPreset ? link.name : OTHER);
    setCustomLabel(isPreset ? "" : link.name);
    setUrl(link.url);
    setFormError(null);
    setIsOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const name = preset === OTHER ? customLabel.trim() : preset;
      if (!name) {
        throw new Error("Enter a label for the link.");
      }
      if (name.length > 100) {
        throw new Error("The label can be at most 100 characters.");
      }
      if (!isValidExternalLinkUrl(url)) {
        throw new Error(
          "Enter a full web address starting with https:// (or http://).",
        );
      }
      if (editing) {
        return updateOrganizationExternalLink(editing.id, { name, url });
      }
      return createOrganizationExternalLink({
        organization_id: organizationId,
        name,
        url,
        position: links.length,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      close();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteOrganizationExternalLink(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const hasAny = !!zuoraLink || links.length > 0;

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">External Links</h3>
        {canEdit && (
          <Button
            variant="secondary"
            size="sm"
            onClick={openAdd}
            data-testid="add-external-link"
          >
            + Add Link
          </Button>
        )}
      </div>
      {loadError && (
        <p className="mb-2 text-sm text-red-600">
          Could not load links:{" "}
          {loadError instanceof Error ? loadError.message : "unknown error"}
        </p>
      )}
      {hasAny ? (
        <ul className="space-y-2" data-testid="external-links-list">
          {zuoraLink && (
            <li className="flex items-center justify-between">
              <ExternalLink href={zuoraLink} label="Zuora Account" />
              <span className="text-xs text-gray-400">
                Edit in Business / Billing
              </span>
            </li>
          )}
          {links.map((link) => (
            <li
              key={link.id}
              className="flex items-center justify-between"
              data-testid={`external-link-${link.id}`}
            >
              <ExternalLink href={link.url} label={link.name} />
              {canEdit && (
                <span className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => openEdit(link)}
                    className="text-gray-500 hover:text-gray-800"
                    aria-label={`Edit ${link.name}`}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Remove the "${link.name}" link?`)) {
                        remove.mutate(link.id);
                      }
                    }}
                    className="text-red-500 hover:text-red-700"
                    aria-label={`Remove ${link.name}`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-center text-gray-500 py-6">
          No external links added yet.
        </p>
      )}

      <Modal
        isOpen={isOpen}
        onClose={close}
        title={editing ? "Edit Link" : "Add Link"}
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError(null);
            save.mutate();
          }}
        >
          <div>
            <label
              htmlFor="external-link-label"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Link
            </label>
            <select
              id="external-link-label"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {EXTERNAL_LINK_PRESETS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
              <option value={OTHER}>Other (type a label)</option>
            </select>
          </div>
          {preset === OTHER && (
            <div>
              <label
                htmlFor="external-link-custom"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Label
              </label>
              <input
                id="external-link-custom"
                value={customLabel}
                maxLength={100}
                onChange={(e) => setCustomLabel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
          <div>
            <label
              htmlFor="external-link-url"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Web address
            </label>
            <input
              id="external-link-url"
              type="url"
              value={url}
              placeholder="https://"
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="mt-1 text-xs text-gray-500">
              For the Admin Portal, paste the facility page address (it contains
              &ldquo;FID=&rdquo;).
            </p>
          </div>
          {formError && (
            <p className="text-sm text-red-600" role="alert">
              {formError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving..." : "Save Link"}
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
};

export default ExternalLinksCard;
